import { Select } from 'components';
import { TrashIcon } from 'icons/TrashIcon';
import React, { FC, ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterRule } from './useQueryBuilder';

export type ColumnType = 'string' | 'date' | 'number' | 'boolean';

export interface FilterColumn {
  field: string;
  label?: string;
  type: ColumnType;
}

export interface FilterBuilderProps {
  columns: FilterColumn[];
  filters: FilterRule[];
  setFilters: React.Dispatch<React.SetStateAction<FilterRule[]>>;
}

// 1. Helper function to avoid nested ternaries
const getOperatorLabel = (type: ColumnType): string => {
  switch (type) {
    case 'number':
      return '=';
    case 'string':
      return 'contains';
    case 'boolean':
    case 'date':
      return 'greater than or equal to';
    default:
      return 'contains';
  }
};

const getOperatorOption = (type: ColumnType) => {
  const label = getOperatorLabel(type);
  return { value: label, label };
};

const FilterBuilder: FC<FilterBuilderProps> = ({
  columns = [],
  filters = [],
  setFilters,
}: FilterBuilderProps) => {
  const [draftFilters, setDraftFilters] = useState<FilterRule[]>(filters);
  const { t } = useTranslation();
  const handleAdd = (): void => {
    const usedFields = draftFilters.map((f) => f.field);
    const nextAvailableField =
      columns.find((col) => !usedFields.includes(col.field))?.field || '';

    if (nextAvailableField) {
      setDraftFilters((prev) => [
        ...prev,
        { id: Date.now(), field: nextAvailableField, value: '' },
      ]);
    }
  };

  const handleDeleteAll = (): void => {
    setDraftFilters([]);
    setFilters([]);
  };

  const handleRemove = (id: number): void => {
    setDraftFilters((prev) => prev.filter((f) => f.id !== id));
    setFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const applyFilters = (): void => {
    setFilters(draftFilters);
  };

  const handleUpdate = (
    id: number,
    key: keyof FilterRule,
    value: string
  ): void => {
    setDraftFilters((prev) =>
      prev.map((filter) => {
        if (filter.id === id) {
          if (key === 'field') return { ...filter, field: value, value: '' };
          return { ...filter, [key]: value };
        }
        return filter;
      })
    );
  };

  // Helper function for rendering dynamic inputs cleanly
  const renderInput = (filter: FilterRule, fieldType: ColumnType) => {
    if (fieldType === 'boolean') {
      const booleanOptions = [
        { value: '', label: `${t('filters.selectStatus')} ...` },
        { value: 'true', label: 'True' },
        { value: 'false', label: 'False' },
      ];

      return (
        <div className="col-sm-12 form-control-bordered padding-zero">
          <Select
            value={booleanOptions.find(
              (option) => option.value === filter.value
            )}
            options={booleanOptions}
            onChange={(e: any) => handleUpdate(filter.id, 'value', e.value)}
          />
        </div>
      );
    }

    const inputType = () => {
      if (fieldType === 'date') return 'date';
      if (fieldType === 'number') return 'number';

      return 'text';
    };

    return (
      <input
        type={inputType()}
        className="col-sm-12 form-control form-control-bordered h-42"
        placeholder="Enter value..."
        value={filter.value}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          handleUpdate(filter.id, 'value', e.target.value)
        }
      />
    );
  };

  return (
    <div className="card mb-3 shadow-sm">
      <div className="card-body p-3 bg-light">
        <div className="pr-1 filters-div">
          {draftFilters.map((filter) => {
            const colConfig =
              columns.find((col) => col.field === filter.field) ||
              columns[0] ||
              {};
            const fieldType: ColumnType = colConfig.type || 'string';
            const columnOptions = columns.map((col) => ({
              value: col.field,
              label: col.label || col.field,
            }));
            const operatorOption = getOperatorOption(fieldType);

            return (
              <div
                key={`filter-${filter.id}`}
                className="form-row align-items-center mb-1"
                style={{ columnGap: '2px' }}
              >
                <div className="col-auto">
                  <button
                    type="button"
                    className="btn btn-link text-muted text-hover-danger p-0 border-0"
                    title="Remove filter"
                    onClick={() => handleRemove(filter.id)}
                  >
                    <TrashIcon />
                  </button>
                </div>

                <div className="col-sm-2 form-control-bordered padding-zero">
                  <Select
                    value={columnOptions.find(
                      (option) => option.value === filter.field
                    )}
                    onChange={(e: any) =>
                      handleUpdate(filter.id, 'field', e.value)
                    }
                    options={columnOptions}
                  />
                </div>

                <div className="col-sm-2 form-control-bordered padding-zero ml-2">
                  <Select
                    value={operatorOption}
                    options={[operatorOption]}
                    isDisabled
                    isSearchable={false}
                    extendedClass="filter-operator-select"
                  />
                </div>

                <div className="col-sm-2 form-control-bordered padding-zero">
                  {renderInput(filter, fieldType)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <button
            type="button"
            className="btn btn-link btn-sm text-decoration-none p-0 mt-1"
            onClick={handleAdd}
          >
            + {t('filters.addFilter')}
          </button>
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn text-danger btn-sm text-decoration-none p-0 mt-1 mb-1 mr-2"
              onClick={handleDeleteAll}
              hidden={draftFilters.length === 0}
            >
              <TrashIcon /> {t('filters.deleteAll')}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm text-decoration-none px-3 py-1 ml-3"
              onClick={applyFilters}
              hidden={draftFilters.length === 0}
            >
              {t('filters.applyFilters')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterBuilder;
